import React, { useEffect, useState } from 'react';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid,
  AreaChart, Area, Cell,
} from 'recharts';
import { TrendingUp, Percent, Clock, ShieldCheck } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { KpiTile } from '@/components/common/KpiTile';
import { KpiRowSkeleton, CardsSkeleton } from '@/components/common/Skeletons';
import api from '@/lib/api';
import { fmtMoney, fmtMoneyShort } from '@/lib/format';

const STATUS_COLORS = {
  Detected: '#94A3B8', Drafted: '#64748B', 'Internal Review': '#6E86E6', Submitted: '#3358D4',
  Negotiation: '#BE7E1B', Approved: '#34A06B', Invoiced: '#1F9C73', Paid: '#15935C',
};

const Tip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-xl border border-[#ECEEF2] bg-white px-3 py-2.5 shadow-lifted text-xs">
      <div className="font-semibold text-ink-900 mb-1.5 font-display">{label}</div>
      {payload.map((p) => (
        <div key={p.name} className="flex items-center gap-2 py-0.5">
          <span className="h-2 w-2 rounded-full" style={{ background: p.color || p.fill }} />
          <span className="text-slate-500">{p.name}</span>
          <span className="num font-semibold text-ink-900 ml-auto pl-4">{fmtMoney(p.value)}</span>
        </div>
      ))}
    </div>
  );
};

const axisTick = { fontSize: 11, fill: '#8A93A3' };

export default function Analytics() {
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => { api.get('/analytics/reporting').then((r) => setD(r.data)).finally(() => setLoading(false)); }, []);
  if (loading) return <div className="space-y-6"><KpiRowSkeleton /><CardsSkeleton count={2} /></div>;
  if (!d) return null;
  const s = d.summary;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        <KpiTile testid="an-secured" label="Total Secured" value={s.secured} variant="recovered" icon={TrendingUp} meta={`of ${fmtMoney(s.identified)} identified`} />
        <KpiTile testid="an-rate" label="Recovery Rate" value={s.recovery_rate} isMoney={false} suffix="%" variant="info" icon={Percent} meta={`${s.ai_detected} of ${s.total_change_orders} COs AI-detected`} />
        <KpiTile testid="an-cycle" label="Avg Cycle Time" value={s.avg_cycle_days} isMoney={false} suffix="d" variant="neutral" icon={Clock} meta="Detection to submission" />
        <KpiTile testid="an-caught" label="Value Caught by AI" value={s.value_caught} variant="at_risk" icon={ShieldCheck} meta="Would otherwise be missed" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="p-5">
          <h3 className="text-sm font-semibold text-ink-900 mb-1">Recovery Over Time</h3>
          <p className="text-xs text-slate-400 mb-4">Dollars secured per month (trailing 8 months)</p>
          <ResponsiveContainer width="100%" height={260}>
            <AreaChart data={d.over_time} margin={{ left: -8, right: 8, top: 4 }}>
              <defs>
                <linearGradient id="recGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#15935C" stopOpacity={0.28} />
                  <stop offset="100%" stopColor="#15935C" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="2 6" stroke="#EDEFF3" vertical={false} />
              <XAxis dataKey="month" tick={axisTick} tickLine={false} axisLine={false} />
              <YAxis tick={axisTick} tickLine={false} axisLine={false} tickFormatter={fmtMoneyShort} width={52} />
              <Tooltip content={<Tip />} cursor={{ stroke: '#CBD5E1', strokeDasharray: '4 4' }} />
              <Area type="monotone" dataKey="recovered" name="Recovered" stroke="#15935C" fill="url(#recGrad)" strokeWidth={2.5} dot={{ r: 3, fill: '#15935C', strokeWidth: 0 }} activeDot={{ r: 5 }} />
            </AreaChart>
          </ResponsiveContainer>
        </Card>

        <Card className="p-5">
          <h3 className="text-sm font-semibold text-ink-900 mb-1">Recovery by Trade</h3>
          <p className="text-xs text-slate-400 mb-4">Secured vs identified value</p>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={d.by_trade} margin={{ left: -8, right: 8 }} barGap={4}>
              <CartesianGrid strokeDasharray="2 6" stroke="#EDEFF3" vertical={false} />
              <XAxis dataKey="trade" tick={{ fontSize: 10, fill: '#8A93A3' }} tickLine={false} axisLine={false} interval={0} />
              <YAxis tick={axisTick} tickLine={false} axisLine={false} tickFormatter={fmtMoneyShort} width={52} />
              <Tooltip content={<Tip />} cursor={{ fill: 'rgba(51,88,212,0.05)' }} />
              <Bar dataKey="identified" name="Identified" fill="#E2E6EE" radius={[5, 5, 0, 0]} maxBarSize={26} />
              <Bar dataKey="recovered" name="Recovered" fill="#15935C" radius={[5, 5, 0, 0]} maxBarSize={26} />
            </BarChart>
          </ResponsiveContainer>
        </Card>

        <Card className="p-5">
          <h3 className="text-sm font-semibold text-ink-900 mb-1">Change Orders by Lifecycle Stage</h3>
          <p className="text-xs text-slate-400 mb-4">Total value at each stage</p>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={d.by_status} margin={{ left: -8, right: 8, bottom: 8 }}>
              <CartesianGrid strokeDasharray="2 6" stroke="#EDEFF3" vertical={false} />
              <XAxis dataKey="status" tick={{ fontSize: 9, fill: '#8A93A3' }} tickLine={false} axisLine={false} interval={0} angle={-18} textAnchor="end" height={52} />
              <YAxis tick={axisTick} tickLine={false} axisLine={false} tickFormatter={fmtMoneyShort} width={52} />
              <Tooltip content={<Tip />} cursor={{ fill: 'rgba(51,88,212,0.05)' }} />
              <Bar dataKey="value" name="Value" radius={[5, 5, 0, 0]} maxBarSize={36}>
                {d.by_status.map((e) => <Cell key={e.status} fill={STATUS_COLORS[e.status] || '#64748B'} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Card>

        <Card className="p-5">
          <h3 className="text-sm font-semibold text-ink-900 mb-1">Recovery by Project</h3>
          <p className="text-xs text-slate-400 mb-4">Secured against identified opportunity</p>
          <div className="space-y-4 mt-3">
            {d.by_project.map((p) => {
              const pct = p.identified ? Math.round((p.recovered / p.identified) * 100) : 0;
              return (
                <div key={p.project}>
                  <div className="flex items-center justify-between text-xs mb-1.5">
                    <span className="text-slate-700 font-medium truncate pr-2">{p.project}</span>
                    <span className="num text-slate-500">{fmtMoney(p.recovered)} <span className="text-slate-300">/</span> {fmtMoney(p.identified)}</span>
                  </div>
                  <div className="h-2.5 rounded-full bg-slate-100 overflow-hidden">
                    <div className="h-full rounded-full bg-gradient-to-r from-money to-money-600 bar-fill" style={{ width: `${pct}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        </Card>
      </div>
    </div>
  );
}
